/**
 * VakantieLagenKaart — vakantiesaldo in lagen op de medewerkerkaart.
 *
 * Toont `beschikbaar` groot en daaronder per laag "wettelijk 2026 · 12u ·
 * vervalt 1 juli 2027". Vervallen lagen grijs doorgestreept, een wettelijke
 * laag zonder kennisgeving (vervallen_geblokkeerd) met uitleg, voorschot rood.
 * Het getal komt uit `GET /api/workforce/vakantie-saldo` (primitief
 * api/_lib/vakantie-saldo.ts), hetzelfde getal als de goedkeurkaart en de
 * team-app.
 */
import { formatHours } from '../../lib/hours-calculator';
import { datumLang, type VakantieSaldo, type LaagStand } from '../../lib/vakantie-saldo';

interface Props {
  titel: string;
  saldo: VakantieSaldo | null;
  laden: boolean;
  /** extra regel onder het getal, bv. de regimesegmenten */
  voetnoot?: React.ReactNode;
}

const LAAG_LABEL: Record<LaagStand['laag'], string> = { wettelijk: 'wettelijk', bovenwettelijk: 'bovenwettelijk' };

function laagNaam(l: LaagStand): string {
  if (l.bron === 'overdracht') return `overdracht ${l.jaar}`;
  if (l.bron === 'potje') return l.omschrijving || `potje ${l.jaar}`;
  return `${LAAG_LABEL[l.laag]} ${l.jaar}`;
}

function LaagRegel({ l }: { l: LaagStand }) {
  const vervallen = l.status === 'vervallen';
  const geblokkeerd = l.status === 'vervallen_geblokkeerd';
  const binnenkort = l.status === 'vervalt_binnenkort';
  const kleur = vervallen
    ? 'text-slate-400 line-through'
    : geblokkeerd
      ? 'text-orange-700'
      : binnenkort
        ? 'text-amber-700'
        : 'text-slate-600';
  const uren = vervallen ? l.vervallen : l.resterend;
  const title = geblokkeerd
    ? 'Nog niet vervallen: er is geen verval-kennisgeving gestuurd (art. 7:640a BW); de uren blijven opneembaar.'
    : vervallen
      ? `Vervallen op ${datumLang(l.vervalt_op)}${l.kennisgeving_op ? `, kennisgeving ${l.kennisgeving_op.slice(0, 10)}` : ''}`
      : `${formatHours(l.uren)} opgebouwd, ${formatHours(l.opgenomen)} opgenomen`;
  return (
    <li className={`flex items-baseline justify-between gap-2 text-[11px] leading-5 ${kleur}`} title={title}>
      <span className="truncate">{laagNaam(l)}</span>
      <span className="shrink-0 tabular-nums">
        {formatHours(uren)}
        <span className="text-slate-400 no-underline"> · {vervallen ? 'vervallen' : geblokkeerd ? 'verval geblokkeerd' : `vervalt ${datumLang(l.vervalt_op)}`}</span>
      </span>
    </li>
  );
}

export function VakantieLagenKaart({ titel, saldo, laden, voetnoot }: Props) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">{titel}</p>
      {!saldo ? (
        <p className="text-sm text-slate-400 mt-2">{laden ? 'Laden…' : 'Geen saldo beschikbaar'}</p>
      ) : (
        <>
          <p className={`text-2xl font-semibold mt-2 ${saldo.beschikbaar >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatHours(saldo.beschikbaar)}
          </p>
          <p className="text-[10px] text-muted mt-1">
            {formatHours(saldo.lagen.reduce((s, l) => s + l.uren, 0))} opgebouwd
            {` − ${formatHours(saldo.opgenomen)} opgenomen`}
            {saldo.vervallen > 0 ? ` − ${formatHours(saldo.vervallen)} vervallen` : ''}
            {saldo.voorschot > 0 && <span className="text-red-600">{` · voorschot ${formatHours(saldo.voorschot)}`}</span>}
            {` · peildatum ${datumLang(saldo.peildatum)}`}
          </p>
          {voetnoot}
          {saldo.lagen.length > 0 && (
            <ul className="mt-3 space-y-0.5 border-t border-gray-100 pt-2">
              {saldo.lagen.map((l, i) => <LaagRegel key={`${l.laag}-${l.jaar}-${l.bron}-${l.entitlement_id ?? i}`} l={l} />)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
